/**
 * Pro 2 live: the "Made for the way you actually live." section from viture.com/pro2 (its "s23").
 * Scales the design stage like the source and drifts the warm glow from -5% to 5% of its height as
 * the section crosses the viewport. Once the section reaches 75% down the viewport (or its bottom
 * passes 25% scrolling back up) the glow fades and settles in, then the title is revealed word by
 * word; it resets when the section leaves the viewport, so it plays on every pass.
 * The fluid backdrop behind it is pro2-fluid.
 * Timeline positions, durations and easing are taken from the source page.
 */
if (!customElements.get('pro2-live')) {
  const MOBILE_QUERY = '(max-width: 640px)';
  const STAGES = { desktop: { width: 1920, height: 1080 }, mobile: { width: 375, height: 812 } };
  const REVEAL_DELAY = 0.7;
  const REVEAL_STAGGER = 0.075;
  // How far the glow drifts either side of rest, as a share of its height
  const DRIFT_PERCENT = 5;
  // Time constant (ms) of the lag behind the scroll position, matching the source's scrub: 0.6
  const SMOOTHING_MS = 200;

  class Pro2Live extends HTMLElement {
    /** @type {MediaQueryList} */
    mobileQuery = window.matchMedia(MOBILE_QUERY);
    /** @type {HTMLElement[]} */
    titles = [];
    /** @type {HTMLElement[]} */
    glows = [];
    resizeFrame = 0;
    /** @type {number | null} */
    animationFrame = null;
    /** @type {number | null} */
    lastTickTime = null;
    currentDrift = 0;
    targetDrift = 0;
    /** @type {IntersectionObserver[]} */
    observers = [];

    connectedCallback() {
      this.titles = Array.from(this.querySelectorAll('[data-split]'));
      this.glows = Array.from(this.querySelectorAll('[data-drift]'));

      window.addEventListener('resize', this.onResize);
      this.mobileQuery.addEventListener('change', this.onResize);
      document.fonts?.ready.then(() => this.titles.forEach((title) => this.#paintGradient(title)));
      this.#fitStage();

      // Reduced motion keeps everything still, with no drift or reveal
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.currentDrift = this.#readDrift();
      this.targetDrift = this.currentDrift;
      this.#renderDrift();

      this.titles.forEach((title) => this.#splitWords(title));
      this.classList.add('is-armed');

      // Play once the section is within the middle half of the viewport, from either direction
      const revealObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.classList.add('is-revealed');
        },
        { rootMargin: '-25% 0px -25% 0px' }
      );
      // Reset, with no transition, once it is fully out of view
      const resetObserver = new IntersectionObserver((entries) => {
        if (entries.every((entry) => !entry.isIntersecting)) this.classList.remove('is-revealed');
      });
      revealObserver.observe(this);
      resetObserver.observe(this);
      this.observers = [revealObserver, resetObserver];
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.mobileQuery.removeEventListener('change', this.onResize);
      this.observers.forEach((observer) => observer.disconnect());
      cancelAnimationFrame(this.resizeFrame);
      if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    onResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.#fitStage();
        this.titles.forEach((title) => this.#paintGradient(title));
        if (this.classList.contains('is-armed')) this.onScroll();
      });
    };

    onScroll = () => {
      this.targetDrift = this.#readDrift();
      if (this.animationFrame === null) this.animationFrame = requestAnimationFrame(this.tick);
    };

    /** Ease the drawn drift toward the scroll position, then stop once it has caught up */
    tick = (/** @type {number} */ now) => {
      const elapsed = Math.min(now - (this.lastTickTime ?? now), 64);
      this.lastTickTime = now;
      const next = this.currentDrift + (this.targetDrift - this.currentDrift) * (1 - Math.exp(-elapsed / SMOOTHING_MS));
      this.currentDrift = Math.abs(this.targetDrift - next) < 0.0005 ? this.targetDrift : next;
      this.#renderDrift();

      if (this.currentDrift === this.targetDrift) {
        this.animationFrame = null;
        this.lastTickTime = null;
      } else {
        this.animationFrame = requestAnimationFrame(this.tick);
      }
    };

    /**
     * Progress from the section's top meeting the viewport bottom (0) to its bottom leaving the top (1)
     * @returns {number}
     */
    #readDrift() {
      const rect = this.getBoundingClientRect();
      const distance = rect.height + window.innerHeight;
      if (distance <= 0) return 0;
      return Math.min(1, Math.max(0, (window.innerHeight - rect.top) / distance));
    }

    #renderDrift() {
      const offset = (this.currentDrift * 2 - 1) * DRIFT_PERCENT;
      this.glows.forEach((glow) => (glow.style.translate = `0 ${offset.toFixed(3)}%`));
    }

    /** Desktop covers the viewport; mobile is 375 wide and as tall as the viewport at that width */
    #fitStage() {
      const viewportWidth = window.innerWidth || this.clientWidth;
      const viewportHeight = this.clientHeight;
      if (this.mobileQuery.matches) {
        const scale = viewportWidth / STAGES.mobile.width;
        this.style.setProperty('--stage-h', (viewportHeight / scale).toFixed(2));
        this.style.setProperty('--scale', scale.toFixed(4));
      } else {
        const { width, height } = STAGES.desktop;
        const scale = Math.max(viewportWidth / width, viewportHeight / height);
        this.style.setProperty('--stage-h', String(height));
        this.style.setProperty('--scale', scale.toFixed(4));
      }
    }

    /**
     * Wrap each word of the title in a span, keeping the spaces between them
     * @param {HTMLElement} title
     */
    #splitWords(title) {
      const text = title.textContent ?? '';
      const fragment = document.createDocumentFragment();
      let wordIndex = 0;
      text.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.append(document.createTextNode(part));
          return;
        }
        const word = document.createElement('span');
        word.className = 'pro2-live__word';
        word.textContent = part;
        const delay = REVEAL_DELAY + wordIndex * REVEAL_STAGGER;
        word.style.setProperty('--word-delay', `${delay.toFixed(3)}s`);
        wordIndex += 1;
        fragment.append(word);
      });
      title.replaceChildren(fragment);
      title.classList.add('is-split');
      this.#paintGradient(title);
    }

    /**
     * Give each word its slice of one gradient spanning the whole title
     * @param {HTMLElement} title
     */
    #paintGradient(title) {
      if (!title.offsetWidth) return;
      const gradientSize = `${title.offsetWidth}px 100%`;
      title.querySelectorAll('.pro2-live__word').forEach((element) => {
        const word = /** @type {HTMLElement} */ (element);
        // The title is positioned, so offsets are from it; take off the reveal's slide-in `left`
        const offsetInTitle = word.offsetLeft - (parseFloat(getComputedStyle(word).left) || 0);
        word.style.backgroundSize = gradientSize;
        word.style.backgroundPosition = `${-offsetInTitle}px 0`;
      });
    }
  }

  customElements.define('pro2-live', Pro2Live);
}
